import { useState, type ReactNode } from 'react';
import type { CategoryNode } from '../api/types';
import type { PeriodNode } from '../lib/period-tree';
import type { FolderPhaseGroup } from '../lib/folder-phases';
import { cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { FolderTree } from './folder-tree';
import { Icon } from './icons';

/**
 * The data room's folder column (admin Data room and seller "Your documents"):
 * the category tree that picks the folder — and, under a folder, the period —
 * the document list shows. With the room checklist's phases, the tree starts
 * at the phase (Financial › Bank Docs › …), and a phase row picks every folder
 * under it. Below 1024px it folds behind a "Folder: …" button so
 * it never pushes the documents below the fold on a phone; picking a folder
 * folds it again. Renders two grid children: the button and the card.
 */
export function DataRoomFolders({
  nodes,
  pending,
  selectedId,
  selectedPeriod,
  periodNodes,
  onSelect,
  onSelectPeriod,
  label,
  headerAction,
  footer,
  phases = null,
  selectedPhase = null,
  onSelectPhase,
}: {
  /** The tree as shown (`folderViewTree`). */
  nodes: CategoryNode[];
  /** The categories are still loading. */
  pending: boolean;
  selectedId: string | null;
  selectedPeriod: string | null;
  periodNodes: ReadonlyMap<string, readonly PeriodNode[]>;
  onSelect: (node: CategoryNode | null) => void;
  onSelectPeriod: (node: CategoryNode, periodKey: string) => void;
  /** What the fold-away button names: "All documents", "Tax Returns › 2024". */
  label: string;
  /** Beside the "Folders" title (the admin's "New folder"). */
  headerAction?: ReactNode;
  /** Under the scrolling tree (the admin's folder audience). */
  footer?: ReactNode;
  /** The checklist phases above the categories (`folderPhaseGroups`). */
  phases?: readonly FolderPhaseGroup[] | null;
  selectedPhase?: string | null;
  onSelectPhase?: (phase: FolderPhaseGroup) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        aria-expanded={open}
        aria-controls="folder-tree-card"
        onClick={() => setOpen((o) => !o)}
        className="flex min-h-tap w-full items-center gap-2 rounded-md border border-input bg-surface px-3 text-left text-sm font-medium lg:hidden"
      >
        <Icon name="folder" className="text-ink-2" />
        <span className="min-w-0 flex-1 truncate">Folder: {label}</span>
        <Icon name="chevronDown" className={cn('transition-transform', open && 'rotate-180')} />
      </button>
      <Card
        id="folder-tree-card"
        className={cn('h-fit lg:sticky lg:top-20 lg:block', open ? 'block' : 'hidden')}
      >
        <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-3">
          <CardTitle>Folders</CardTitle>
          {headerAction}
        </CardHeader>
        {/* The tree scrolls; it leaves room for a footer below it, so the sticky
            card never runs past the bottom of the screen. */}
        <CardContent
          className={cn(
            'px-3 lg:overflow-y-auto',
            footer ? 'lg:max-h-[calc(100vh-19rem)]' : 'lg:max-h-[calc(100vh-12rem)]',
          )}
        >
          {pending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : nodes.length > 0 ? (
            <FolderTree
              nodes={nodes}
              selectedId={selectedId}
              onSelect={(node) => {
                onSelect(node);
                setOpen(false);
              }}
              periodNodes={periodNodes}
              selectedPeriod={selectedPeriod}
              onSelectPeriod={(node, periodKey) => {
                onSelectPeriod(node, periodKey);
                setOpen(false);
              }}
              phases={phases}
              selectedPhase={selectedPhase}
              onSelectPhase={(phase) => {
                onSelectPhase?.(phase);
                setOpen(false);
              }}
            />
          ) : (
            <p className="text-sm text-muted-foreground">No categories yet.</p>
          )}
        </CardContent>
        {footer ? <div className="px-4 pb-4">{footer}</div> : null}
      </Card>
    </>
  );
}
